<template>
  <div>
    <!-- Permission Denied -->
    <div v-if="!permission || !permission.view_assets" class="permission-denied">
      <font-awesome-icon icon="lock" class="denied-icon" />
      <p class="denied-title">Access Denied</p>
      <p class="denied-sub">You don't have permission to view the Asset module.</p>
    </div>

    <template v-else>
    <!-- Page Header -->
    <div class="page-header">
      <div>
        <h5 class="page-title">Asset Management</h5>
        <p class="page-sub">Track and manage company assets throughout their lifecycle</p>
      </div>
      <div class="page-actions">
        <button v-if="permission.manage_assets" class="btn-outline-action" @click="openRunDeprModal">
          <font-awesome-icon icon="calculator" /> Run Depreciation
        </button>
        <button v-if="permission.manage_assets" class="btn-primary-action" @click="openCreateAsset">
          <font-awesome-icon icon="plus" /> Add Asset
        </button>
      </div>
    </div>

    <!-- Tab Nav -->
    <div class="asset-tabs">
      <button v-for="t in tabs" :key="t.key" class="asset-tab" :class="{ 'asset-tab--active': activeTab === t.key }" @click="switchTab(t.key)">
        <font-awesome-icon :icon="t.icon" class="me-1" /> {{ t.label }}
      </button>
    </div>

    <!-- ══════════════════════════════════════════════════════════════
         TAB: DASHBOARD
    ══════════════════════════════════════════════════════════════ -->
    <template v-if="activeTab === 'dashboard'">
      <div v-if="dashLoading" class="sk-grid">
        <div v-for="n in 4" :key="n" class="sk-stat-card"></div>
      </div>
      <template v-else>
        <div class="stat-grid">
          <div class="stat-card">
            <div class="stat-icon stat-icon--blue"><font-awesome-icon icon="boxes" /></div>
            <div class="stat-body">
              <div class="stat-value">{{ totalAssets }}</div>
              <div class="stat-label">Total Assets</div>
            </div>
          </div>
          <div class="stat-card">
            <div class="stat-icon stat-icon--green"><font-awesome-icon icon="check-circle" /></div>
            <div class="stat-body">
              <div class="stat-value">{{ dash.counts_by_status?.available ?? 0 }}</div>
              <div class="stat-label">Available</div>
            </div>
          </div>
          <div class="stat-card">
            <div class="stat-icon stat-icon--amber"><font-awesome-icon icon="user-check" /></div>
            <div class="stat-body">
              <div class="stat-value">{{ dash.counts_by_status?.allocated ?? 0 }}</div>
              <div class="stat-label">Allocated</div>
            </div>
          </div>
          <div class="stat-card">
            <div class="stat-icon stat-icon--red"><font-awesome-icon icon="exclamation-triangle" /></div>
            <div class="stat-body">
              <div class="stat-value">{{ (dash.counts_by_status?.lost ?? 0) + (dash.counts_by_status?.damaged ?? 0) }}</div>
              <div class="stat-label">Lost / Damaged</div>
            </div>
          </div>
          <div class="stat-card">
            <div class="stat-icon stat-icon--purple"><font-awesome-icon :icon="currentUserBusiness.country_code == 'NG'? 'naira-sign' : 'pound-sign'" /></div>
            <div class="stat-body">
              <div class="stat-value">{{ fmtCost(dash.total_purchase_cost) }}</div>
              <div class="stat-label">Total Purchase Cost</div>
            </div>
          </div>
          <div class="stat-card">
            <div class="stat-icon stat-icon--teal"><font-awesome-icon icon="chart-line" /></div>
            <div class="stat-body">
              <div class="stat-value">{{ fmtCost(dash.total_net_book_value) }}</div>
              <div class="stat-label">Total Net Book Value</div>
            </div>
          </div>
        </div>

        <div class="section-header mt-3">
          <p class="section-title">Recent Allocations</p>
        </div>
        <div class="table-card">
          <table class="data-table">
            <thead><tr><th>Asset</th><th>Assigned To</th><th>Type</th><th>Allocated</th><th>Due Return</th></tr></thead>
            <tbody>
              <tr v-for="a in dash.recent_allocations" :key="a.id">
                <td>{{ a.asset?.name ?? '—' }} <span class="text-muted-sm">{{ a.asset?.asset_no }}</span></td>
                <td>{{ assigneeName(a) }}</td>
                <td><span class="chip chip--sm" :class="assigneeTypeChip(a.assignee_type)">{{ a.assignee_type ?? 'employee' }}</span></td>
                <td>{{ fmtDate(a.allocated_at) }}</td>
                <td>{{ a.return_due_date ? fmtDate(a.return_due_date) : '—' }}</td>
              </tr>
              <tr v-if="!dash.recent_allocations?.length"><td colspan="5" class="empty-cell">No recent allocations</td></tr>
            </tbody>
          </table>
        </div>
      </template>
    </template>

    <!-- ══════════════════════════════════════════════════════════════
         TAB: ASSET REGISTER
    ══════════════════════════════════════════════════════════════ -->
    <template v-if="activeTab === 'assets'">
      <div class="filter-bar">
        <input v-model="assetSearch" type="text" class="filter-input" placeholder="Search name, serial, asset no…" @input="debouncedAssetSearch" />
        <select v-model="assetStatusFilter" class="filter-select" @change="fetchAssets">
          <option value="">All Statuses</option>
          <option v-for="s in assetStatuses" :key="s.value" :value="s.value">{{ s.label }}</option>
        </select>
        <select v-model="assetCategoryFilter" class="filter-select" @change="fetchAssets">
          <option value="">All Categories</option>
          <option v-for="c in categories" :key="c.id" :value="c.id">{{ c.name }}</option>
        </select>
      </div>

      <div v-if="assetsLoading" class="sk-table"><div v-for="n in 5" :key="n" class="sk-row"></div></div>
      <div v-else class="table-card">
        <table class="data-table">
          <thead>
            <tr>
              <th>Asset No</th><th>Name</th><th>Category</th><th>Status</th>
              <th>Purchase Cost</th><th>NBV</th><th>Custodian</th><th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="a in assets" :key="a.id">
              <td class="fw-600 text-blue">{{ a.asset_no }}</td>
              <td>
                <div class="fw-600">{{ a.name }}</div>
                <div class="text-muted-sm">{{ a.brand }} {{ a.model_no }}</div>
              </td>
              <td>{{ a.category?.name ?? '—' }}</td>
              <td><span class="chip" :class="statusChip(a.status)">{{ fmtStatus(a.status) }}</span></td>
              <td>{{ a.purchase_cost ? fmtCost(a.purchase_cost) : '—' }}</td>
              <td>{{ a.net_book_value != null ? fmtCost(a.net_book_value) : '—' }}</td>
              <td>{{ a.active_allocation ? assigneeName(a.active_allocation) : '—' }}</td>
              <td class="actions-cell">
                <button class="btn-icon" title="View" @click="viewAsset(a)"><font-awesome-icon icon="eye" /></button>
                <button v-if="a.depreciation_method !== 'none'" class="btn-icon btn-icon--teal" title="Depreciation Schedule" @click="openDeprSchedule(a)"><font-awesome-icon icon="chart-line" /></button>
                <button v-if="permission.manage_assets" class="btn-icon" title="Edit" @click="openEditAsset(a)"><font-awesome-icon icon="pen" /></button>
                <button v-if="permission.manage_assets && a.status === 'available'" class="btn-icon" title="Allocate" @click="openAllocateFromAsset(a)"><font-awesome-icon icon="user-plus" /></button>
                <button v-if="permission.manage_assets && a.status === 'available'" class="btn-icon btn-icon--red" title="Delete" @click="deleteAsset(a)"><font-awesome-icon icon="trash" /></button>
              </td>
            </tr>
            <tr v-if="!assets.length"><td colspan="8" class="empty-cell">No assets found</td></tr>
          </tbody>
        </table>
        <div class="pagination-bar" v-if="assetsMeta.last_page > 1">
          <button class="btn-pg" :disabled="assetsMeta.current_page === 1" @click="assetPage--; fetchAssets()">‹ Prev</button>
          <span class="pg-info">Page {{ assetsMeta.current_page }} of {{ assetsMeta.last_page }}</span>
          <button class="btn-pg" :disabled="assetsMeta.current_page === assetsMeta.last_page" @click="assetPage++; fetchAssets()">Next ›</button>
        </div>
      </div>
    </template>

    <!-- ══════════════════════════════════════════════════════════════
         TAB: ALLOCATIONS
    ══════════════════════════════════════════════════════════════ -->
    <template v-if="activeTab === 'allocations'">
      <div class="section-header">
        <p class="section-title">Asset Allocations</p>
        <button v-if="permission.manage_assets" class="btn-primary-action" @click="openAllocateModal"><font-awesome-icon icon="plus" /> Allocate Asset</button>
      </div>
      <div class="filter-bar">
        <select v-model="allocStatusFilter" class="filter-select" @change="fetchAllocations">
          <option value="">All</option>
          <option value="active">Active</option>
          <option value="returned">Returned</option>
        </select>
      </div>
      <div v-if="allocLoading" class="sk-table"><div v-for="n in 5" :key="n" class="sk-row"></div></div>
      <div v-else class="table-card">
        <table class="data-table">
          <thead>
            <tr><th>Asset</th><th>Assigned To</th><th>Type</th><th>Allocated</th><th>Due Return</th><th>Status</th><th>Actions</th></tr>
          </thead>
          <tbody>
            <tr v-for="al in allocations" :key="al.id">
              <td>
                <div class="fw-600">{{ al.asset?.name }}</div>
                <div class="text-muted-sm">{{ al.asset?.asset_no }}</div>
              </td>
              <td>{{ assigneeName(al) }}</td>
              <td><span class="chip chip--sm" :class="assigneeTypeChip(al.assignee_type)">{{ al.assignee_type ?? 'employee' }}</span></td>
              <td>{{ fmtDate(al.allocated_at) }}</td>
              <td>{{ al.return_due_date ? fmtDate(al.return_due_date) : '—' }}</td>
              <td><span class="chip" :class="al.status === 'active' ? 'chip--green' : 'chip--gray'">{{ al.status }}</span></td>
              <td class="actions-cell">
                <button v-if="permission.manage_assets && al.status === 'active'" class="btn-icon" title="Return Asset" @click="openReturnModal(al)">
                  <font-awesome-icon icon="undo" />
                </button>
              </td>
            </tr>
            <tr v-if="!allocations.length"><td colspan="6" class="empty-cell">No allocations found</td></tr>
          </tbody>
        </table>
        <div class="pagination-bar" v-if="allocMeta.last_page > 1">
          <button class="btn-pg" :disabled="allocMeta.current_page === 1" @click="allocPage--; fetchAllocations()">‹ Prev</button>
          <span class="pg-info">Page {{ allocMeta.current_page }} of {{ allocMeta.last_page }}</span>
          <button class="btn-pg" :disabled="allocMeta.current_page === allocMeta.last_page" @click="allocPage++; fetchAllocations()">Next ›</button>
        </div>
      </div>
    </template>

    <!-- ══════════════════════════════════════════════════════════════
         TAB: LOSS & DAMAGE
    ══════════════════════════════════════════════════════════════ -->
    <template v-if="activeTab === 'loss-damage'">
      <div class="section-header">
        <p class="section-title">Loss &amp; Damage Cases</p>
        <button v-if="permission.manage_assets" class="btn-primary-action" @click="openRaiseCaseModal"><font-awesome-icon icon="plus" /> Raise Case</button>
      </div>
      <div v-if="ldLoading" class="sk-table"><div v-for="n in 4" :key="n" class="sk-row"></div></div>
      <div v-else class="table-card">
        <table class="data-table">
          <thead>
            <tr><th>Asset</th><th>Employee</th><th>Incident Date</th><th>Assessed Cost</th><th>Recovery</th><th>Status</th><th>Actions</th></tr>
          </thead>
          <tbody>
            <tr v-for="c in ldCases" :key="c.id">
              <td><div class="fw-600">{{ c.asset?.name }}</div><div class="text-muted-sm">{{ c.asset?.asset_no }}</div></td>
              <td>{{ employeeName(c.employee) }}</td>
              <td>{{ fmtDate(c.incident_date) }}</td>
              <td>{{ c.assessed_cost ? fmtCost(c.assessed_cost) : '—' }}</td>
              <td>{{ c.recovery_amount ? fmtCost(c.recovery_amount) : '—' }}<span v-if="c.installments > 1" class="text-muted-sm"> / {{ c.installments }} inst.</span></td>
              <td><span class="chip" :class="ldChip(c.status)">{{ fmtStatus(c.status) }}</span></td>
              <td class="actions-cell">
                <template v-if="c.status === 'pending'">
                  <button v-if="permission.manage_assets" class="btn-icon btn-icon--green" title="Approve" @click="approveLd(c)"><font-awesome-icon icon="check" /></button>
                  <button v-if="permission.manage_assets" class="btn-icon btn-icon--red" title="Reject" @click="rejectLd(c)"><font-awesome-icon icon="times" /></button>
                </template>
              </td>
            </tr>
            <tr v-if="!ldCases.length"><td colspan="7" class="empty-cell">No loss/damage cases</td></tr>
          </tbody>
        </table>
        <div class="pagination-bar" v-if="ldMeta.last_page > 1">
          <button class="btn-pg" :disabled="ldMeta.current_page === 1" @click="ldPage--; fetchLd()">‹ Prev</button>
          <span class="pg-info">Page {{ ldMeta.current_page }} of {{ ldMeta.last_page }}</span>
          <button class="btn-pg" :disabled="ldMeta.current_page === ldMeta.last_page" @click="ldPage++; fetchLd()">Next ›</button>
        </div>
      </div>
    </template>

    <!-- ══════════════════════════════════════════════════════════════
         TAB: DISPOSALS
    ══════════════════════════════════════════════════════════════ -->
    <template v-if="activeTab === 'disposals'">
      <div class="section-header">
        <p class="section-title">Asset Disposals</p>
        <button v-if="permission.manage_assets" class="btn-primary-action" @click="openDisposalModal"><font-awesome-icon icon="plus" /> Create Disposal</button>
      </div>
      <div v-if="dispLoading" class="sk-table"><div v-for="n in 4" :key="n" class="sk-row"></div></div>
      <div v-else class="table-card">
        <table class="data-table">
          <thead>
            <tr><th>Asset</th><th>Method</th><th>Date</th><th>Sale Value</th><th>Gain / Loss</th><th>Status</th><th>Actions</th></tr>
          </thead>
          <tbody>
            <tr v-for="d in disposals" :key="d.id">
              <td><div class="fw-600">{{ d.asset?.name }}</div><div class="text-muted-sm">{{ d.asset?.asset_no }}</div></td>
              <td>{{ fmtMethod(d.disposal_method) }}</td>
              <td>{{ fmtDate(d.disposal_date) }}</td>
              <td>{{ fmtCost(d.sale_value) }}</td>
              <td :class="d.gain_loss >= 0 ? 'text-green' : 'text-red'">{{ fmtCost(Math.abs(d.gain_loss)) }} {{ d.gain_loss >= 0 ? 'gain' : 'loss' }}</td>
              <td><span class="chip" :class="disposalChip(d.status)">{{ fmtStatus(d.status) }}</span></td>
              <td class="actions-cell">
                <template v-if="d.status === 'pending'">
                  <button v-if="permission.manage_assets" class="btn-icon btn-icon--green" title="Approve" @click="approveDisposal(d)"><font-awesome-icon icon="check" /></button>
                  <button v-if="permission.manage_assets" class="btn-icon btn-icon--red" title="Reject" @click="rejectDisposal(d)"><font-awesome-icon icon="times" /></button>
                </template>
              </td>
            </tr>
            <tr v-if="!disposals.length"><td colspan="7" class="empty-cell">No disposal records</td></tr>
          </tbody>
        </table>
      </div>
    </template>

    <!-- ══════════════════════════════════════════════════════════════
         TAB: MAINTENANCE
    ══════════════════════════════════════════════════════════════ -->
    <template v-if="activeTab === 'maintenance'">
      <div class="section-header">
        <p class="section-title">Maintenance Logs</p>
        <button v-if="permission.manage_assets" class="btn-primary-action" @click="openMaintenanceModal"><font-awesome-icon icon="plus" /> Log Issue</button>
      </div>
      <div v-if="maintLoading" class="sk-table"><div v-for="n in 4" :key="n" class="sk-row"></div></div>
      <div v-else class="table-card">
        <table class="data-table">
          <thead>
            <tr><th>Asset</th><th>Issue Type</th><th>Vendor</th><th>Reported</th><th>Resolved</th><th>Status</th><th>Actions</th></tr>
          </thead>
          <tbody>
            <tr v-for="m in maintenance" :key="m.id">
              <td><div class="fw-600">{{ m.asset?.name }}</div><div class="text-muted-sm">{{ m.asset?.asset_no }}</div></td>
              <td>{{ m.issue_type }}</td>
              <td>{{ m.asset_vendor?.name ?? '—' }}</td>
              <td>{{ fmtDate(m.reported_date) }}</td>
              <td>{{ m.resolved_date ? fmtDate(m.resolved_date) : '—' }}</td>
              <td><span class="chip" :class="maintChip(m.status)">{{ fmtStatus(m.status) }}</span></td>
              <td class="actions-cell">
                <button v-if="permission.manage_assets && m.status !== 'resolved'" class="btn-icon btn-icon--green" title="Mark Resolved" @click="resolveMaintenance(m)"><font-awesome-icon icon="check" /></button>
              </td>
            </tr>
            <tr v-if="!maintenance.length"><td colspan="7" class="empty-cell">No maintenance logs</td></tr>
          </tbody>
        </table>
      </div>
    </template>

    <!-- ══════════════════════════════════════════════════════════════
         TAB: CATEGORIES
    ══════════════════════════════════════════════════════════════ -->
    <template v-if="activeTab === 'categories'">
      <div class="section-header">
        <p class="section-title">Asset Categories</p>
        <button v-if="permission.manage_assets" class="btn-primary-action" @click="openCreateCategory"><font-awesome-icon icon="plus" /> New Category</button>
      </div>
      <div v-if="catLoading" class="sk-table"><div v-for="n in 4" :key="n" class="sk-row"></div></div>
      <div v-else class="table-card">
        <table class="data-table">
          <thead>
            <tr><th>Name</th><th>Parent</th><th>Depreciation</th><th>Useful Life</th><th>Status</th><th>Assets</th><th>Actions</th></tr>
          </thead>
          <tbody>
            <tr v-for="c in categories" :key="c.id">
              <td class="fw-600">{{ c.name }}</td>
              <td>{{ c.parent?.name ?? '—' }}</td>
              <td>{{ fmtDepMethod(c.depreciation_method) }}</td>
              <td>{{ c.default_useful_life_months ? (c.default_useful_life_months / 12).toFixed(1) + ' yr' : '—' }}</td>
              <td><span class="chip" :class="c.is_active ? 'chip--green' : 'chip--gray'">{{ c.is_active ? 'Active' : 'Inactive' }}</span></td>
              <td>{{ c.assets_count ?? 0 }}</td>
              <td class="actions-cell">
                <button v-if="permission.manage_assets" class="btn-icon" title="Edit" @click="openEditCategory(c)"><font-awesome-icon icon="pen" /></button>
                <button v-if="permission.manage_assets" class="btn-icon btn-icon--red" title="Delete" @click="deleteCategory(c)"><font-awesome-icon icon="trash" /></button>
              </td>
            </tr>
            <tr v-if="!categories.length"><td colspan="7" class="empty-cell">No categories yet</td></tr>
          </tbody>
        </table>
      </div>
    </template>

    <!-- ══════════════════════════════════════════════════════════════
         TAB: VENDORS
    ══════════════════════════════════════════════════════════════ -->
    <template v-if="activeTab === 'vendors'">
      <div class="section-header">
        <p class="section-title">Asset Vendors</p>
        <button v-if="permission.manage_assets" class="btn-primary-action" @click="openCreateVendor"><font-awesome-icon icon="plus" /> New Vendor</button>
      </div>
      <div class="filter-bar">
        <input v-model="vendorSearch" type="text" class="filter-input" placeholder="Search vendors…" @input="filterVendors" />
      </div>
      <div v-if="vendorLoading" class="sk-table"><div v-for="n in 5" :key="n" class="sk-row"></div></div>
      <div v-else class="table-card">
        <table class="data-table">
          <thead>
            <tr><th>Name</th><th>Category</th><th>Contact Person</th><th>Email</th><th>Phone</th><th>Status</th><th>Actions</th></tr>
          </thead>
          <tbody>
            <tr v-for="v in filteredVendorList" :key="v.id">
              <td class="fw-600">{{ v.name }}</td>
              <td>{{ v.category?.name ?? '—' }}</td>
              <td>{{ v.contact_person ?? '—' }}</td>
              <td>{{ v.email ?? '—' }}</td>
              <td>{{ v.phone ?? '—' }}</td>
              <td><span class="chip" :class="v.is_active ? 'chip--green' : 'chip--gray'">{{ v.is_active ? 'Active' : 'Inactive' }}</span></td>
              <td class="actions-cell">
                <button v-if="permission.manage_assets" class="btn-icon" title="Edit" @click="openEditVendor(v)"><font-awesome-icon icon="pen" /></button>
                <button v-if="permission.manage_assets" class="btn-icon btn-icon--red" title="Delete" @click="deleteVendor(v)"><font-awesome-icon icon="trash" /></button>
              </td>
            </tr>
            <tr v-if="!filteredVendorList.length"><td colspan="7" class="empty-cell">No vendors found</td></tr>
          </tbody>
        </table>
      </div>
    </template>

    <!-- ══════════════════════════════════════════════════════════════
         MODALS
    ══════════════════════════════════════════════════════════════ -->

    <!-- Asset View Modal -->
    <div class="modal-overlay" :class="{ open: viewModal }" @click.self="viewModal = false">
      <div class="modal-box modal-box--wide">
        <div class="modal-header-styled">
          <span class="modal-title-styled">{{ viewedAsset.name }} <span class="text-muted-sm">{{ viewedAsset.asset_no }}</span></span>
          <button class="modal-close-btn" @click="viewModal = false"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body-styled asset-detail-grid">
          <div class="detail-row"><span class="detail-label">Category</span><span>{{ viewedAsset.category?.name ?? '—' }}</span></div>
          <div class="detail-row"><span class="detail-label">Status</span><span class="chip" :class="statusChip(viewedAsset.status)">{{ fmtStatus(viewedAsset.status) }}</span></div>
          <div class="detail-row"><span class="detail-label">Brand / Model</span><span>{{ [viewedAsset.brand, viewedAsset.model_no].filter(Boolean).join(' / ') || '—' }}</span></div>
          <div class="detail-row"><span class="detail-label">Serial No</span><span>{{ viewedAsset.serial_no ?? '—' }}</span></div>
          <div class="detail-row"><span class="detail-label">Purchase Date</span><span>{{ viewedAsset.purchase_date ? fmtDate(viewedAsset.purchase_date) : '—' }}</span></div>
          <div class="detail-row"><span class="detail-label">Purchase Cost</span><span>{{ viewedAsset.purchase_cost ? fmtCost(viewedAsset.purchase_cost) : '—' }}</span></div>
          <div class="detail-row"><span class="detail-label">Net Book Value</span><span class="fw-600 text-blue">{{ viewedAsset.net_book_value != null ? fmtCost(viewedAsset.net_book_value) : '—' }}</span></div>
          <div class="detail-row"><span class="detail-label">Vendor</span><span>{{ viewedAsset.asset_vendor?.name ?? viewedAsset.vendor ?? '—' }}</span></div>
          <div class="detail-row"><span class="detail-label">Warranty Expiry</span><span>{{ viewedAsset.warranty_expiry ? fmtDate(viewedAsset.warranty_expiry) : '—' }}</span></div>
          <div class="detail-row"><span class="detail-label">Location</span><span>{{ viewedAsset.location?.name ?? viewedAsset.location_label ?? viewedAsset.location ?? '—' }}</span></div>
          <div class="detail-row"><span class="detail-label">Depreciation</span><span>{{ fmtDepMethod(viewedAsset.depreciation_method) }}</span></div>
          <div class="detail-row"><span class="detail-label">Useful Life</span><span>{{ viewedAsset.useful_life_months ? (viewedAsset.useful_life_months / 12).toFixed(1) + ' years' : '—' }}</span></div>
          <div class="detail-row"><span class="detail-label">Salvage Value</span><span>{{ viewedAsset.salvage_value != null ? fmtCost(viewedAsset.salvage_value) : '—' }}</span></div>
          <div class="detail-row"><span class="detail-label">Accum. Depreciation</span><span class="text-red">{{ viewedAsset.accumulated_depreciation ? fmtCost(viewedAsset.accumulated_depreciation) : '—' }}</span></div>
          <div class="detail-row"><span class="detail-label">Last Depreciated</span><span>{{ viewedAsset.last_depreciation_date ? fmtDate(viewedAsset.last_depreciation_date) : 'Never' }}</span></div>
        </div>
        <div class="modal-footer-styled">
          <button v-if="viewedAsset.depreciation_method !== 'none'" class="btn-ghost" @click="openDeprSchedule(viewedAsset); viewModal = false;">
            <font-awesome-icon icon="chart-line" class="me-1" /> View Schedule
          </button>
          <button class="btn-ghost" @click="viewModal = false">Close</button>
        </div>
      </div>
    </div>

    <!-- Create/Edit Asset Modal -->
    <div class="modal-overlay" :class="{ open: assetModal }" @click.self="assetModal = false">
      <div class="modal-box modal-box--wide">
        <div class="modal-header-styled">
          <span class="modal-title-styled">{{ editingAsset ? 'Edit Asset' : 'Add New Asset' }}</span>
          <button class="modal-close-btn" @click="assetModal = false"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body-styled form-grid-2col">
          <div class="form-group-styled">
            <label class="form-label-styled">Asset Name <span class="req">*</span></label>
            <input v-model="assetForm.name" type="text" class="form-input-styled" placeholder="e.g. Dell Latitude 5540" />
          </div>
          <div class="form-group-styled">
            <label class="form-label-styled">Category</label>
            <select v-model="assetSelectedParentId" class="form-input-styled" @change="onParentCatChange">
              <option value="">— Select Category —</option>
              <option v-for="c in parentCategories" :key="c.id" :value="c.id">{{ c.name }}</option>
            </select>
          </div>
          <div class="form-group-styled" v-if="assetSelectedParentId && subCategories.length">
            <label class="form-label-styled">Sub Category</label>
            <select v-model="assetForm.category_id" class="form-input-styled">
              <option value="">— Select Sub Category —</option>
              <option v-for="c in subCategories" :key="c.id" :value="c.id">{{ c.name }}</option>
            </select>
          </div>
          <div class="form-group-styled">
            <label class="form-label-styled">Brand</label>
            <input v-model="assetForm.brand" type="text" class="form-input-styled" placeholder="e.g. Dell" />
          </div>
          <div class="form-group-styled">
            <label class="form-label-styled">Model No</label>
            <input v-model="assetForm.model_no" type="text" class="form-input-styled" placeholder="e.g. 5540-XXX" />
          </div>
          <div class="form-group-styled">
            <label class="form-label-styled">Serial No</label>
            <input v-model="assetForm.serial_no" type="text" class="form-input-styled" />
          </div>
          <div class="form-group-styled">
            <label class="form-label-styled">Barcode</label>
            <input v-model="assetForm.barcode" type="text" class="form-input-styled" />
          </div>
          <div class="form-group-styled">
            <label class="form-label-styled">Purchase Date</label>
            <input v-model="assetForm.purchase_date" type="date" class="form-input-styled" />
          </div>
          <div class="form-group-styled">
            <label class="form-label-styled">Purchase Cost</label>
            <input v-model="assetForm.purchase_cost" type="number" min="0" step="0.01" class="form-input-styled" />
          </div>
          <div class="form-group-styled">
            <label class="form-label-styled">Vendor</label>
            <select v-model="assetForm.vendor_id" class="form-input-styled">
              <option value="">— Select Vendor —</option>
              <option v-for="v in filteredVendors" :key="v.id" :value="v.id">{{ v.name }}</option>
            </select>
          </div>
          <div class="form-group-styled">
            <label class="form-label-styled">Warranty Expiry</label>
            <input v-model="assetForm.warranty_expiry" type="date" class="form-input-styled" />
          </div>
          <div class="form-group-styled">
            <label class="form-label-styled">Location</label>
            <select v-model="assetForm.location_id" class="form-input-styled">
              <option value="">— Select Location —</option>
              <option v-for="loc in locations" :key="loc.id" :value="loc.id">{{ loc.name }}</option>
            </select>
          </div>
          <div class="form-group-styled">
            <label class="form-label-styled">Acquisition Type</label>
            <select v-model="assetForm.acquisition_type" class="form-input-styled">
              <option value="purchased">Purchased</option>
              <option value="leased">Leased</option>
              <option value="rented">Rented</option>
              <option value="company_provided">Company Provided</option>
              <option value="byod">BYOD</option>
            </select>
          </div>
          <div class="form-group-styled">
            <label class="form-label-styled">Depreciation Method</label>
            <select v-model="assetForm.depreciation_method" class="form-input-styled">
              <option value="straight_line">Straight-Line</option>
              <option value="declining_balance">Declining Balance (WDV)</option>
              <option value="units_of_production">Units of Production</option>
              <option value="none">None (Expensed)</option>
            </select>
          </div>
          <div class="form-group-styled">
            <label class="form-label-styled">Useful Life (months)</label>
            <input v-model="assetForm.useful_life_months" type="number" min="1" class="form-input-styled" placeholder="e.g. 36 for 3 years" />
          </div>
          <div class="form-group-styled">
            <label class="form-label-styled">Salvage Value</label>
            <input v-model="assetForm.salvage_value" type="number" min="0" step="0.01" class="form-input-styled" placeholder="Residual value at end of life" />
          </div>
          <div class="form-group-styled span-2">
            <label class="form-label-styled">Notes</label>
            <textarea v-model="assetForm.notes" class="form-input-styled" rows="2"></textarea>
          </div>
        </div>
        <div class="modal-footer-styled">
          <button class="btn-ghost" @click="assetModal = false">Cancel</button>
          <button class="btn-primary-action" :disabled="savingAsset" @click="saveAsset">
            <font-awesome-icon v-if="savingAsset" icon="spinner" spin class="me-1" />
            {{ savingAsset ? 'Saving…' : (editingAsset ? 'Update Asset' : 'Create Asset') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Allocate Asset Modal -->
    <div class="modal-overlay" :class="{ open: allocModal }" @click.self="allocModal = false">
      <div class="modal-box">
        <div class="modal-header-styled">
          <span class="modal-title-styled">Allocate Asset</span>
          <button class="modal-close-btn" @click="allocModal = false"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body-styled">
          <div class="form-group-styled">
            <label class="form-label-styled">Asset <span class="req">*</span></label>
            <select v-model="allocForm.asset_id" class="form-input-styled" :disabled="!!allocForm._locked_asset">
              <option value="">— Select Available Asset —</option>
              <option v-for="a in availableAssets" :key="a.id" :value="a.id">{{ a.asset_no }} — {{ a.name }}</option>
            </select>
          </div>
          <div class="form-group-styled">
            <label class="form-label-styled">Assign To <span class="req">*</span></label>
            <div class="assignee-type-tabs">
              <button type="button" :class="['assignee-tab', allocForm.assignee_type === 'employee' && 'assignee-tab--active']" @click="allocForm.assignee_type = 'employee'; clearAllocAssignee()">Employee</button>
              <button type="button" :class="['assignee-tab', allocForm.assignee_type === 'location' && 'assignee-tab--active']" @click="allocForm.assignee_type = 'location'; clearAllocAssignee()">Location</button>
              <button type="button" :class="['assignee-tab', allocForm.assignee_type === 'client' && 'assignee-tab--active']" @click="allocForm.assignee_type = 'client'; clearAllocAssignee()">Client</button>
            </div>
          </div>
          <!-- Employee -->
          <div v-if="allocForm.assignee_type === 'employee'" class="form-group-styled">
            <label class="form-label-styled">Employee <span class="req">*</span></label>
            <input v-model="empSearch" type="text" class="form-input-styled" placeholder="Search employee…" @input="searchEmployees" />
            <div v-if="empResults.length" class="emp-dropdown">
              <div v-for="e in empResults" :key="e.id" class="emp-option" @click="selectEmployee(e)">{{ e.name }}</div>
            </div>
            <div v-if="allocForm.employee_name" class="selected-employee">
              <font-awesome-icon icon="user-check" class="me-1 text-green" /> {{ allocForm.employee_name }}
              <button class="btn-clear" @click="clearEmployee">×</button>
            </div>
          </div>
          <!-- Location -->
          <div v-if="allocForm.assignee_type === 'location'" class="form-group-styled">
            <label class="form-label-styled">Location <span class="req">*</span></label>
            <select v-model="allocForm.location_id" class="form-input-styled">
              <option value="">— Select Location —</option>
              <option v-for="loc in locations" :key="loc.id" :value="loc.id">{{ loc.name }}</option>
            </select>
          </div>
          <!-- Client -->
          <div v-if="allocForm.assignee_type === 'client'" class="form-group-styled">
            <label class="form-label-styled">Client <span class="req">*</span></label>
            <input v-model="allocClientSearch" type="text" class="form-input-styled" placeholder="Search client…" @input="searchAllocClients" />
            <div v-if="allocClientResults.length" class="emp-dropdown">
              <div v-for="c in allocClientResults" :key="c.id" class="emp-option" @click="selectAllocClient(c)">{{ c.customer_name }}</div>
            </div>
            <div v-if="allocForm.client_name" class="selected-employee">
              <font-awesome-icon icon="building" class="me-1 text-blue" /> {{ allocForm.client_name }}
              <button class="btn-clear" @click="allocForm.client_id = ''; allocForm.client_name = ''">×</button>
            </div>
          </div>
          <div class="form-group-styled">
            <label class="form-label-styled">Expected Return Date</label>
            <input v-model="allocForm.return_due_date" type="date" class="form-input-styled" />
          </div>
          <div class="form-group-styled">
            <label class="form-label-styled">Notes</label>
            <textarea v-model="allocForm.notes" class="form-input-styled" rows="2"></textarea>
          </div>
        </div>
        <div class="modal-footer-styled">
          <button class="btn-ghost" @click="allocModal = false">Cancel</button>
          <button class="btn-primary-action" :disabled="savingAlloc" @click="submitAllocation">
            <font-awesome-icon v-if="savingAlloc" icon="spinner" spin class="me-1" />
            {{ savingAlloc ? 'Allocating…' : 'Allocate Asset' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Return Asset Modal -->
    <div class="modal-overlay" :class="{ open: returnModal }" @click.self="returnModal = false">
      <div class="modal-box">
        <div class="modal-header-styled">
          <span class="modal-title-styled">Return Asset</span>
          <button class="modal-close-btn" @click="returnModal = false"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body-styled">
          <p class="mb-3" style="font-size:13px;color:#4a5e78;">Returning <strong>{{ returningAlloc.asset?.name }}</strong> from <strong>{{ assigneeName(returningAlloc) }}</strong></p>
          <div class="form-group-styled">
            <label class="form-label-styled">Condition at Return <span class="req">*</span></label>
            <select v-model="returnForm.condition_at_return" class="form-input-styled">
              <option value="">— Select Condition —</option>
              <option value="good">Good</option>
              <option value="damaged">Damaged</option>
              <option value="lost">Lost</option>
            </select>
          </div>
          <div class="form-group-styled">
            <label class="form-label-styled">Return Notes</label>
            <textarea v-model="returnForm.return_notes" class="form-input-styled" rows="2"></textarea>
          </div>
        </div>
        <div class="modal-footer-styled">
          <button class="btn-ghost" @click="returnModal = false">Cancel</button>
          <button class="btn-primary-action" :disabled="savingReturn" @click="submitReturn">
            <font-awesome-icon v-if="savingReturn" icon="spinner" spin class="me-1" />
            {{ savingReturn ? 'Processing…' : 'Confirm Return' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Raise Loss/Damage Case Modal -->
    <div class="modal-overlay" :class="{ open: ldModal }" @click.self="ldModal = false">
      <div class="modal-box">
        <div class="modal-header-styled">
          <span class="modal-title-styled">Raise Loss / Damage Case</span>
          <button class="modal-close-btn" @click="ldModal = false"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body-styled">
          <div class="form-group-styled">
            <label class="form-label-styled">Asset <span class="req">*</span></label>
            <select v-model="ldForm.asset_id" class="form-input-styled">
              <option value="">— Select Asset —</option>
              <option v-for="a in assets" :key="a.id" :value="a.id">{{ a.asset_no }} — {{ a.name }}</option>
            </select>
          </div>
          <div class="form-group-styled">
            <label class="form-label-styled">Employee <span class="req">*</span></label>
            <input v-model="ldEmpSearch" type="text" class="form-input-styled" placeholder="Search employee…" @input="searchLdEmployees" />
            <div v-if="ldEmpResults.length" class="emp-dropdown">
              <div v-for="e in ldEmpResults" :key="e.id" class="emp-option" @click="selectLdEmployee(e)">{{ e.name }}</div>
            </div>
            <div v-if="ldForm.employee_name" class="selected-employee">
              <font-awesome-icon icon="user-check" class="me-1 text-green" /> {{ ldForm.employee_name }}
              <button class="btn-clear" @click="ldForm.employee_id = ''; ldForm.employee_name = ''">×</button>
            </div>
          </div>
          <div class="form-group-styled">
            <label class="form-label-styled">Case Type</label>
            <select v-model="ldForm.case_type" class="form-input-styled">
              <option value="damaged">Damaged</option>
              <option value="lost">Lost</option>
            </select>
          </div>
          <div class="form-group-styled">
            <label class="form-label-styled">Incident Date <span class="req">*</span></label>
            <input v-model="ldForm.incident_date" type="date" class="form-input-styled" />
          </div>
          <div class="form-group-styled">
            <label class="form-label-styled">Description <span class="req">*</span></label>
            <textarea v-model="ldForm.description" class="form-input-styled" rows="2"></textarea>
          </div>
          <div class="form-group-styled">
            <label class="form-label-styled">Assessed Cost</label>
            <input v-model="ldForm.assessed_cost" type="number" min="0" step="0.01" class="form-input-styled" />
          </div>
          <div class="form-group-styled">
            <label class="form-label-styled">Recovery Amount</label>
            <input v-model="ldForm.recovery_amount" type="number" min="0" step="0.01" class="form-input-styled" />
          </div>
          <div class="form-group-styled">
            <label class="form-label-styled">Installments</label>
            <input v-model="ldForm.installments" type="number" min="1" class="form-input-styled" placeholder="1 = lump sum" />
          </div>
        </div>
        <div class="modal-footer-styled">
          <button class="btn-ghost" @click="ldModal = false">Cancel</button>
          <button class="btn-primary-action" :disabled="savingLd" @click="submitLdCase">
            <font-awesome-icon v-if="savingLd" icon="spinner" spin class="me-1" />
            {{ savingLd ? 'Saving…' : 'Raise Case' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Disposal Modal -->
    <div class="modal-overlay" :class="{ open: disposalModal }" @click.self="disposalModal = false">
      <div class="modal-box">
        <div class="modal-header-styled">
          <span class="modal-title-styled">Create Disposal Record</span>
          <button class="modal-close-btn" @click="disposalModal = false"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body-styled">
          <div class="form-group-styled">
            <label class="form-label-styled">Asset <span class="req">*</span></label>
            <select v-model="disposalForm.asset_id" class="form-input-styled">
              <option value="">— Select Asset —</option>
              <option v-for="a in assets" :key="a.id" :value="a.id">{{ a.asset_no }} — {{ a.name }}</option>
            </select>
          </div>
          <div class="form-group-styled">
            <label class="form-label-styled">Disposal Method <span class="req">*</span></label>
            <select v-model="disposalForm.disposal_method" class="form-input-styled">
              <option value="sale">Sale</option>
              <option value="scrap">Scrap</option>
              <option value="donation">Donation</option>
              <option value="write_off">Write-off</option>
              <option value="insurance_claim">Insurance Claim</option>
            </select>
          </div>
          <div class="form-group-styled">
            <label class="form-label-styled">Disposal Date <span class="req">*</span></label>
            <input v-model="disposalForm.disposal_date" type="date" class="form-input-styled" />
          </div>
          <div class="form-group-styled">
            <label class="form-label-styled">Sale Value</label>
            <input v-model="disposalForm.sale_value" type="number" min="0" step="0.01" class="form-input-styled" />
          </div>
          <div class="form-group-styled">
            <label class="form-label-styled">Reason</label>
            <textarea v-model="disposalForm.reason" class="form-input-styled" rows="2"></textarea>
          </div>
        </div>
        <div class="modal-footer-styled">
          <button class="btn-ghost" @click="disposalModal = false">Cancel</button>
          <button class="btn-primary-action" :disabled="savingDisposal" @click="submitDisposal">
            <font-awesome-icon v-if="savingDisposal" icon="spinner" spin class="me-1" />
            {{ savingDisposal ? 'Saving…' : 'Create Disposal' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Maintenance Modal -->
    <div class="modal-overlay" :class="{ open: maintModal }" @click.self="maintModal = false">
      <div class="modal-box">
        <div class="modal-header-styled">
          <span class="modal-title-styled">Log Maintenance Issue</span>
          <button class="modal-close-btn" @click="maintModal = false"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body-styled">
          <div class="form-group-styled">
            <label class="form-label-styled">Asset <span class="req">*</span></label>
            <select v-model="maintForm.asset_id" class="form-input-styled">
              <option value="">— Select Asset —</option>
              <option v-for="a in assets" :key="a.id" :value="a.id">{{ a.asset_no }} — {{ a.name }}</option>
            </select>
          </div>
          <div class="form-group-styled">
            <label class="form-label-styled">Issue Type <span class="req">*</span></label>
            <input v-model="maintForm.issue_type" type="text" class="form-input-styled" placeholder="e.g. Screen Damage, Battery Failure" />
          </div>
          <div class="form-group-styled">
            <label class="form-label-styled">Reported Date <span class="req">*</span></label>
            <input v-model="maintForm.reported_date" type="date" class="form-input-styled" />
          </div>
          <div class="form-group-styled">
            <label class="form-label-styled">Description</label>
            <textarea v-model="maintForm.description" class="form-input-styled" rows="2"></textarea>
          </div>
          <div class="form-group-styled">
            <label class="form-label-styled">Vendor</label>
            <select v-model="maintForm.vendor_id" class="form-input-styled">
              <option value="">— Select Vendor —</option>
              <option v-for="v in vendors" :key="v.id" :value="v.id">{{ v.name }}</option>
            </select>
          </div>
          <div class="form-group-styled">
            <label class="form-label-styled">
              <input type="checkbox" v-model="maintForm.set_in_maintenance" class="me-1" />
              Set asset status to "In Maintenance"
            </label>
          </div>
        </div>
        <div class="modal-footer-styled">
          <button class="btn-ghost" @click="maintModal = false">Cancel</button>
          <button class="btn-primary-action" :disabled="savingMaint" @click="submitMaintenance">
            <font-awesome-icon v-if="savingMaint" icon="spinner" spin class="me-1" />
            {{ savingMaint ? 'Saving…' : 'Log Issue' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Category Modal -->
    <div class="modal-overlay" :class="{ open: catModal }" @click.self="catModal = false">
      <div class="modal-box">
        <div class="modal-header-styled">
          <span class="modal-title-styled">{{ editingCat ? 'Edit Category' : 'New Category' }}</span>
          <button class="modal-close-btn" @click="catModal = false"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body-styled">
          <div class="form-group-styled">
            <label class="form-label-styled">Name <span class="req">*</span></label>
            <input v-model="catForm.name" type="text" class="form-input-styled" placeholder="e.g. IT Equipment" />
          </div>
          <div class="form-group-styled">
            <label class="form-label-styled">Parent Category</label>
            <select v-model="catForm.parent_id" class="form-input-styled">
              <option value="">— None (top-level) —</option>
              <option v-for="c in categories.filter(x => x.id !== editingCat?.id)" :key="c.id" :value="c.id">{{ c.name }}</option>
            </select>
          </div>
          <div class="form-group-styled">
            <label class="form-label-styled">Default Depreciation Method</label>
            <select v-model="catForm.depreciation_method" class="form-input-styled">
              <option value="straight_line">Straight-Line</option>
              <option value="declining_balance">Declining Balance (WDV)</option>
              <option value="units_of_production">Units of Production</option>
              <option value="none">None (Expensed)</option>
            </select>
          </div>
          <div class="form-group-styled">
            <label class="form-label-styled">Default Useful Life (months)</label>
            <input v-model="catForm.default_useful_life_months" type="number" min="1" class="form-input-styled" placeholder="e.g. 36" />
          </div>
          <div class="form-group-styled">
            <label class="form-label-styled">Description</label>
            <textarea v-model="catForm.description" class="form-input-styled" rows="2"></textarea>
          </div>
          <div class="form-group-styled">
            <label class="form-label-styled">
              <input type="checkbox" v-model="catForm.is_active" class="me-1" /> Active
            </label>
          </div>
        </div>
        <div class="modal-footer-styled">
          <button class="btn-ghost" @click="catModal = false">Cancel</button>
          <button class="btn-primary-action" :disabled="savingCat" @click="saveCat">
            <font-awesome-icon v-if="savingCat" icon="spinner" spin class="me-1" />
            {{ savingCat ? 'Saving…' : (editingCat ? 'Update' : 'Create') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Vendor Modal -->
    <div class="modal-overlay" :class="{ open: vendorModal }" @click.self="vendorModal = false">
      <div class="modal-box">
        <div class="modal-header-styled">
          <span class="modal-title-styled">{{ editingVendor ? 'Edit Vendor' : 'New Vendor' }}</span>
          <button class="modal-close-btn" @click="vendorModal = false"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body-styled form-grid-2col">
          <div class="form-group-styled">
            <label class="form-label-styled">Vendor Name <span class="req">*</span></label>
            <input v-model="vendorForm.name" type="text" class="form-input-styled" placeholder="e.g. Dell Technologies" />
          </div>
          <div class="form-group-styled">
            <label class="form-label-styled">Category</label>
            <select v-model="vendorForm.category_id" class="form-input-styled">
              <option value="">— None —</option>
              <option v-for="c in parentCategories" :key="c.id" :value="c.id">{{ c.name }}</option>
            </select>
          </div>
          <div class="form-group-styled">
            <label class="form-label-styled">Contact Person</label>
            <input v-model="vendorForm.contact_person" type="text" class="form-input-styled" placeholder="e.g. John Smith" />
          </div>
          <div class="form-group-styled">
            <label class="form-label-styled">Phone</label>
            <input v-model="vendorForm.phone" type="text" class="form-input-styled" placeholder="+1-800-000-0000" />
          </div>
          <div class="form-group-styled" style="grid-column:1/-1">
            <label class="form-label-styled">Email</label>
            <input v-model="vendorForm.email" type="email" class="form-input-styled" placeholder="sales@vendor.com" />
          </div>
          <div class="form-group-styled" style="grid-column:1/-1">
            <label class="form-label-styled">Address</label>
            <textarea v-model="vendorForm.address" class="form-input-styled" rows="2" placeholder="Street, City, Country (optional)"></textarea>
          </div>
          <div class="form-group-styled" style="grid-column:1/-1">
            <label class="form-label-styled">
              <input type="checkbox" v-model="vendorForm.is_active" class="me-1" /> Active
            </label>
          </div>
        </div>
        <div class="modal-footer-styled">
          <button class="btn-ghost" @click="vendorModal = false">Cancel</button>
          <button class="btn-primary-action" :disabled="savingVendor" @click="saveVendor">
            <font-awesome-icon v-if="savingVendor" icon="spinner" spin class="me-1" />
            {{ savingVendor ? 'Saving…' : (editingVendor ? 'Update' : 'Create') }}
          </button>
        </div>
      </div>
    </div>

    <!-- ── Depreciation Schedule Modal ────────────────────────────── -->
    <div class="modal-overlay" :class="{ open: deprModal }" @click.self="deprModal = false">
      <div class="modal-box modal-box--wide">
        <div class="modal-header-styled">
          <span class="modal-title-styled">
            <font-awesome-icon icon="chart-line" class="me-1" />
            Depreciation Schedule — {{ deprAsset.name }}
          </span>
          <button class="modal-close-btn" @click="deprModal = false"><font-awesome-icon icon="times" /></button>
        </div>

        <!-- Summary strip -->
        <div class="depr-summary-strip" v-if="deprAsset.id">
          <div class="depr-kv"><span class="depr-k">Method</span><span class="depr-v">{{ fmtDepMethod(deprAsset.depreciation_method) }}</span></div>
          <div class="depr-kv"><span class="depr-k">Cost</span><span class="depr-v">{{ fmtCost(deprAsset.purchase_cost) }}</span></div>
          <div class="depr-kv"><span class="depr-k">Salvage Value</span><span class="depr-v">{{ fmtCost(deprAsset.salvage_value ?? 0) }}</span></div>
          <div class="depr-kv"><span class="depr-k">Useful Life</span><span class="depr-v">{{ deprAsset.useful_life_months ? (deprAsset.useful_life_months / 12).toFixed(1) + ' yrs' : '—' }}</span></div>
          <div class="depr-kv"><span class="depr-k">Accum. Depreciation</span><span class="depr-v text-red">{{ fmtCost(deprAsset.accumulated_depreciation ?? 0) }}</span></div>
          <div class="depr-kv"><span class="depr-k">Net Book Value</span><span class="depr-v text-blue fw-600">{{ fmtCost(deprAsset.net_book_value) }}</span></div>
        </div>

        <div v-if="deprLoading" class="depr-loading">
          <span class="spinner-border spinner-border-sm me-2" role="status"></span> Loading schedule…
        </div>

        <div v-else-if="!deprSchedule.length" class="depr-empty">
          No depreciation schedule available for this asset. Ensure purchase cost, useful life, and depreciation method are set.
        </div>

        <div v-else class="depr-table-wrap">
          <table class="depr-table">
            <thead>
              <tr>
                <th>Period</th>
                <th class="tr">Charge</th>
                <th class="tr">Accumulated</th>
                <th class="tr">Net Book Value</th>
                <th class="tc">Status</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in deprSchedule" :key="row.period" :class="{ 'depr-row--posted': row.posted, 'depr-row--future': !row.posted }">
                <td class="fw-600">{{ row.period }}</td>
                <td class="tr">{{ fmtCost(row.charge) }}</td>
                <td class="tr text-red">{{ fmtCost(row.accumulated_depreciation) }}</td>
                <td class="tr text-blue fw-600">{{ fmtCost(row.net_book_value) }}</td>
                <td class="tc">
                  <span class="chip" :class="row.posted ? 'chip--green' : 'chip--gray'">
                    {{ row.posted ? 'Posted' : 'Projected' }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="modal-footer-styled">
          <button class="btn-ghost" @click="deprModal = false">Close</button>
        </div>
      </div>
    </div>

    <!-- ── Run Depreciation Modal ───────────────────────────────────── -->
    <div class="modal-overlay" :class="{ open: runDeprModal }" @click.self="runDeprModal = false">
      <div class="modal-box">
        <div class="modal-header-styled">
          <span class="modal-title-styled">Run Monthly Depreciation</span>
          <button class="modal-close-btn" @click="runDeprModal = false"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body-styled">
          <p style="font-size:13px;color:#4a5e78;margin:0 0 12px;">
            This will post depreciation journals for all eligible assets for the selected month.
            Assets already depreciated for that month are skipped.
          </p>
          <div class="form-group-styled">
            <label class="form-label-styled">Period (Month)</label>
            <input v-model="runDeprMonth" type="month" class="form-input-styled" />
          </div>
          <div v-if="runDeprResult" class="depr-result-box" :class="runDeprResult.hasErrors ? 'depr-result--warn' : 'depr-result--ok'">
            <div><strong>Processed:</strong> {{ runDeprResult.processed }}</div>
            <div><strong>Skipped:</strong> {{ runDeprResult.skipped }}</div>
            <div v-if="runDeprResult.errors.length"><strong>Errors:</strong>
              <ul class="depr-err-list">
                <li v-for="(e, i) in runDeprResult.errors" :key="i">{{ e }}</li>
              </ul>
            </div>
          </div>
        </div>
        <div class="modal-footer-styled">
          <button class="btn-ghost" @click="runDeprModal = false">Close</button>
          <button class="btn-primary-action" :disabled="runDeprLoading" @click="submitRunDepreciation">
            <font-awesome-icon v-if="runDeprLoading" icon="spinner" spin class="me-1" />
            {{ runDeprLoading ? 'Running…' : 'Run Depreciation' }}
          </button>
        </div>
      </div>
    </div>

    </template><!-- end v-else (has view_assets) -->
  </div>
</template>

<script>
import axios from 'axios';
import { useToast } from 'vue-toastification';
import { reusableMixin } from '../../../../lib/reusableMixin';

export default {
  name: 'AssetManagement',
  mixins: [reusableMixin],

  data() {
    return {
      toast: useToast(),
      activeTab: 'dashboard',

      tabs: [
        { key: 'dashboard',   label: 'Dashboard',    icon: 'chart-bar' },
        { key: 'assets',      label: 'Asset Register', icon: 'boxes' },
        { key: 'allocations', label: 'Allocations',   icon: 'user-check' },
        { key: 'loss-damage', label: 'Loss & Damage', icon: 'exclamation-triangle' },
        { key: 'disposals',   label: 'Disposals',     icon: 'trash-alt' },
        { key: 'maintenance', label: 'Maintenance',   icon: 'tools' },
        { key: 'categories',  label: 'Categories',    icon: 'tag' },
        { key: 'vendors',     label: 'Vendors',       icon: 'truck' },
      ],

      // Dashboard
      dash: {},
      dashLoading: false,

      // Assets
      assets: [],
      assetsMeta: { current_page: 1, last_page: 1 },
      assetsLoading: false,
      assetPage: 1,
      assetSearch: '',
      assetStatusFilter: '',
      assetCategoryFilter: '',
      assetSearchTimer: null,

      // Available assets for allocation dropdown
      availableAssets: [],

      // Categories
      categories: [],
      catLoading: false,

      // Vendors
      vendors: [],

      // Locations (from DB)
      locations: [],

      // Allocations
      allocations: [],
      allocMeta: { current_page: 1, last_page: 1 },
      allocLoading: false,
      allocPage: 1,
      allocStatusFilter: 'active',
      allocClientSearch: '',
      allocClientResults: [],
      allocClientTimer: null,

      // Loss & Damage
      ldCases: [],
      ldMeta: { current_page: 1, last_page: 1 },
      ldLoading: false,
      ldPage: 1,

      // Disposals
      disposals: [],
      dispLoading: false,

      // Maintenance
      maintenance: [],
      maintLoading: false,

      // Modals
      viewModal: false,
      viewedAsset: {},

      assetModal: false,
      editingAsset: null,
      assetForm: this.blankAssetForm(),
      assetSelectedParentId: '',
      savingAsset: false,

      allocModal: false,
      allocForm: this.blankAllocForm(),
      savingAlloc: false,
      empSearch: '',
      empResults: [],
      empSearchTimer: null,

      returnModal: false,
      returningAlloc: {},
      returnForm: { condition_at_return: '', return_notes: '' },
      savingReturn: false,

      ldModal: false,
      ldForm: this.blankLdForm(),
      savingLd: false,
      ldEmpSearch: '',
      ldEmpResults: [],

      disposalModal: false,
      disposalForm: this.blankDisposalForm(),
      savingDisposal: false,

      maintModal: false,
      maintForm: this.blankMaintForm(),
      savingMaint: false,

      catModal: false,
      editingCat: null,
      catForm: this.blankCatForm(),
      savingCat: false,

      vendorModal: false,
      editingVendor: null,
      vendorForm: this.blankVendorForm(),
      savingVendor: false,
      vendorLoading: false,
      vendorSearch: '',

      // Depreciation schedule modal
      deprModal: false,
      deprAsset: {},
      deprSchedule: [],
      deprLoading: false,

      // Run depreciation modal
      runDeprModal: false,
      runDeprMonth: new Date().toISOString().slice(0, 7),
      runDeprLoading: false,
      runDeprResult: null,

      assetStatuses: [
        { value: 'available',     label: 'Available' },
        { value: 'allocated',     label: 'Allocated' },
        { value: 'in_maintenance', label: 'In Maintenance' },
        { value: 'lost',          label: 'Lost' },
        { value: 'damaged',       label: 'Damaged' },
        { value: 'disposed',      label: 'Disposed' },
        { value: 'archived',      label: 'Archived' },
      ],
    };
  },

  computed: {
    totalAssets() {
      if (!this.dash.counts_by_status) return 0;
      return Object.values(this.dash.counts_by_status).reduce((s, v) => s + v, 0);
    },
    parentCategories() {
      return this.categories.filter(c => !c.parent_id);
    },
    subCategories() {
      if (!this.assetSelectedParentId) return [];
      return this.categories.filter(c => c.parent_id === this.assetSelectedParentId);
    },
    filteredVendors() {
      if (!this.assetSelectedParentId) return this.vendors.filter(v => v.is_active);
      return this.vendors.filter(v => v.is_active && v.category_id === this.assetSelectedParentId);
    },
    filteredVendorList() {
      if (!this.vendorSearch.trim()) return this.vendors;
      const s = this.vendorSearch.toLowerCase();
      return this.vendors.filter(v =>
        v.name?.toLowerCase().includes(s) ||
        v.contact_person?.toLowerCase().includes(s) ||
        v.email?.toLowerCase().includes(s) ||
        v.category?.name?.toLowerCase().includes(s)
      );
    },
  },

  mounted() {
    this.fetchCategories();
    this.fetchVendors();
    this.fetchLocations();
    this.fetchDashboard();
  },

  methods: {
    // ── Tab switching ──────────────────────────────────────────────
    switchTab(key) {
      this.activeTab = key;
      if (key === 'dashboard')   this.fetchDashboard();
      if (key === 'assets')      this.fetchAssets();
      if (key === 'allocations') this.fetchAllocations();
      if (key === 'loss-damage') this.fetchLd();
      if (key === 'disposals')   this.fetchDisposals();
      if (key === 'maintenance') this.fetchMaintenance();
      if (key === 'categories')  this.fetchCategories();
      if (key === 'vendors')     this.fetchVendors();
    },

    // ── Dashboard ──────────────────────────────────────────────────
    async fetchDashboard() {
      this.dashLoading = true;
      try {
        const { data } = await axios.get('/hr/asset/dashboard');
        this.dash = data.data ?? {};
      } catch { this.toast.error('Failed to load dashboard'); }
      finally { this.dashLoading = false; }
    },

    // ── Assets ─────────────────────────────────────────────────────
    async fetchAssets() {
      this.assetsLoading = true;
      try {
        const params = { page: this.assetPage, per_page: 15 };
        if (this.assetSearch)          params.search      = this.assetSearch;
        if (this.assetStatusFilter)    params.status      = this.assetStatusFilter;
        if (this.assetCategoryFilter)  params.category_id = this.assetCategoryFilter;
        const { data } = await axios.get('/hr/asset', { params });
        this.assets    = data.data?.data ?? [];
        this.assetsMeta = { current_page: data.data?.current_page ?? 1, last_page: data.data?.last_page ?? 1 };
      } catch { this.toast.error('Failed to load assets'); }
      finally { this.assetsLoading = false; }
    },

    async fetchAvailableAssets() {
      try {
        const { data } = await axios.get('/hr/asset', { params: { status: 'available', per_page: 100 } });
        this.availableAssets = data.data?.data ?? [];
      } catch { /**/ }
    },

    debouncedAssetSearch() {
      clearTimeout(this.assetSearchTimer);
      this.assetSearchTimer = setTimeout(() => { this.assetPage = 1; this.fetchAssets(); }, 350);
    },

    viewAsset(a) {
      // Fetch full detail (includes NBV)
      axios.get(`/hr/asset/${a.id}`)
        .then(({ data }) => { this.viewedAsset = data.data ?? a; this.viewModal = true; })
        .catch(() => { this.viewedAsset = a; this.viewModal = true; });
    },

    openCreateAsset() {
      this.editingAsset = null;
      this.assetForm = this.blankAssetForm();
      this.assetSelectedParentId = '';
      this.assetModal = true;
    },

    openEditAsset(a) {
      this.editingAsset = a;
      this.assetForm = {
        ...a,
        category_id: a.category_id ?? '',
        vendor_id:   a.vendor_id   ?? '',
        location_id: a.location_id ?? '',
      };
      const cat = this.categories.find(c => c.id === a.category_id);
      if (cat) {
        this.assetSelectedParentId = cat.parent_id || cat.id;
      } else {
        this.assetSelectedParentId = '';
      }
      this.assetModal = true;
    },

    onParentCatChange() {
      this.assetForm.category_id = '';
      this.assetForm.vendor_id   = '';
      // If this parent has no children, use it directly as the category
      const hasSubs = this.categories.some(c => c.parent_id === this.assetSelectedParentId);
      if (!hasSubs && this.assetSelectedParentId) {
        this.assetForm.category_id = this.assetSelectedParentId;
      }
    },

    async saveAsset() {
      if (!this.assetForm.name?.trim()) { this.toast.warning('Asset name is required'); return; }
      this.savingAsset = true;
      try {
        if (this.editingAsset) {
          await axios.put(`/hr/asset/${this.editingAsset.id}`, this.assetForm);
          this.toast.success('Asset updated');
        } else {
          await axios.post('/hr/asset', this.assetForm);
          this.toast.success('Asset created');
        }
        this.assetModal = false;
        this.fetchAssets();
        this.fetchDashboard();
      } catch (e) { this.toast.error(e.response?.data?.message ?? 'Save failed'); }
      finally { this.savingAsset = false; }
    },

    async deleteAsset(a) {
      if (!confirm(`Delete asset "${a.name}"? This cannot be undone.`)) return;
      try {
        await axios.delete(`/hr/asset/${a.id}`);
        this.toast.success('Asset deleted');
        this.fetchAssets(); this.fetchDashboard();
      } catch (e) { this.toast.error(e.response?.data?.message ?? 'Delete failed'); }
    },

    // ── Categories ─────────────────────────────────────────────────
    async fetchCategories() {
      this.catLoading = true;
      try {
        const { data } = await axios.get('/hr/asset/category');
        this.categories = data.data ?? [];
      } catch { /**/ }
      finally { this.catLoading = false; }
    },

    async fetchVendors() {
      this.vendorLoading = true;
      try {
        const { data } = await axios.get('/hr/asset/vendor');
        this.vendors = data.data ?? [];
      } catch { /**/ }
      finally { this.vendorLoading = false; }
    },

    filterVendors() { /* reactive via filteredVendorList computed */ },

    openCreateVendor() {
      this.editingVendor = null;
      this.vendorForm = this.blankVendorForm();
      this.vendorModal = true;
    },

    openEditVendor(v) {
      this.editingVendor = v;
      this.vendorForm = {
        name:           v.name,
        category_id:    v.category_id   ?? '',
        contact_person: v.contact_person ?? '',
        email:          v.email         ?? '',
        phone:          v.phone         ?? '',
        address:        v.address       ?? '',
        is_active:      v.is_active     ?? true,
      };
      this.vendorModal = true;
    },

    async saveVendor() {
      if (!this.vendorForm.name?.trim()) { this.toast.warning('Vendor name is required'); return; }
      this.savingVendor = true;
      try {
        if (this.editingVendor) {
          await axios.put(`/hr/asset/vendor/${this.editingVendor.id}`, this.vendorForm);
          this.toast.success('Vendor updated');
        } else {
          await axios.post('/hr/asset/vendor', this.vendorForm);
          this.toast.success('Vendor created');
        }
        this.vendorModal = false;
        this.fetchVendors();
      } catch (e) { this.toast.error(e.response?.data?.message ?? 'Save failed'); }
      finally { this.savingVendor = false; }
    },

    async deleteVendor(v) {
      if (!confirm(`Delete vendor "${v.name}"?`)) return;
      try {
        await axios.delete(`/hr/asset/vendor/${v.id}`);
        this.toast.success('Vendor deleted');
        this.fetchVendors();
      } catch (e) { this.toast.error(e.response?.data?.message ?? 'Delete failed'); }
    },

    async fetchLocations() {
      try {
        const { data } = await axios.get('/admin/location/active');
        this.locations = data.data ?? [];
      } catch { /**/ }
    },

    openCreateCategory() { this.editingCat = null; this.catForm = this.blankCatForm(); this.catModal = true; },
    openEditCategory(c)  { this.editingCat = c; this.catForm = { ...c, parent_id: c.parent_id ?? '' }; this.catModal = true; },

    async saveCat() {
      if (!this.catForm.name?.trim()) { this.toast.warning('Category name is required'); return; }
      this.savingCat = true;
      try {
        if (this.editingCat) {
          await axios.put(`/hr/asset/category/${this.editingCat.id}`, this.catForm);
          this.toast.success('Category updated');
        } else {
          await axios.post('/hr/asset/category', this.catForm);
          this.toast.success('Category created');
        }
        this.catModal = false;
        this.fetchCategories();
      } catch (e) { this.toast.error(e.response?.data?.message ?? 'Save failed'); }
      finally { this.savingCat = false; }
    },

    async deleteCategory(c) {
      if (!confirm(`Delete category "${c.name}"?`)) return;
      try {
        await axios.delete(`/hr/asset/category/${c.id}`);
        this.toast.success('Category deleted');
        this.fetchCategories();
      } catch (e) { this.toast.error(e.response?.data?.message ?? 'Delete failed'); }
    },

    // ── Allocations ────────────────────────────────────────────────
    async fetchAllocations() {
      this.allocLoading = true;
      try {
        const params = { page: this.allocPage, per_page: 15 };
        if (this.allocStatusFilter) params.status = this.allocStatusFilter;
        const { data } = await axios.get('/hr/asset/allocation', { params });
        this.allocations = data.data?.data ?? [];
        this.allocMeta   = { current_page: data.data?.current_page ?? 1, last_page: data.data?.last_page ?? 1 };
      } catch { this.toast.error('Failed to load allocations'); }
      finally { this.allocLoading = false; }
    },

    openAllocateModal() {
      this.allocForm = this.blankAllocForm();
      this.empSearch = ''; this.empResults = [];
      this.allocClientSearch = ''; this.allocClientResults = [];
      this.fetchAvailableAssets();
      this.allocModal = true;
    },

    openAllocateFromAsset(a) {
      this.allocForm = { ...this.blankAllocForm(), asset_id: a.id, _locked_asset: true };
      this.empSearch = ''; this.empResults = [];
      this.allocClientSearch = ''; this.allocClientResults = [];
      this.allocModal = true;
    },

    async searchEmployees() {
      clearTimeout(this.empSearchTimer);
      if (!this.empSearch.trim()) { this.empResults = []; return; }
      this.empSearchTimer = setTimeout(async () => {
        try {
          const { data } = await axios.get('/hr/chat/employees', { params: { search: this.empSearch } });
          this.empResults = data.data ?? [];
        } catch { /**/ }
      }, 300);
    },

    selectEmployee(e) { this.allocForm.employee_id = e.id; this.allocForm.employee_name = e.name; this.empSearch = ''; this.empResults = []; },
    clearEmployee()   { this.allocForm.employee_id = ''; this.allocForm.employee_name = ''; },

    async submitAllocation() {
      if (!this.allocForm.asset_id) { this.toast.warning('Select an asset'); return; }
      const type = this.allocForm.assignee_type;
      if (type === 'employee' && !this.allocForm.employee_id) { this.toast.warning('Select an employee'); return; }
      if (type === 'location' && !this.allocForm.location_id) { this.toast.warning('Select a location'); return; }
      if (type === 'client'   && !this.allocForm.client_id)   { this.toast.warning('Select a client'); return; }
      this.savingAlloc = true;
      try {
        await axios.post('/hr/asset/allocation', {
          asset_id:       this.allocForm.asset_id,
          assignee_type:  type,
          employee_id:    type === 'employee' ? this.allocForm.employee_id : undefined,
          location_id:    type === 'location' ? this.allocForm.location_id : undefined,
          client_id:      type === 'client'   ? this.allocForm.client_id   : undefined,
          return_due_date: this.allocForm.return_due_date,
          notes:          this.allocForm.notes,
        });
        this.toast.success('Asset allocated successfully');
        this.allocModal = false;
        this.fetchAllocations(); this.fetchAssets(); this.fetchDashboard();
      } catch (e) { this.toast.error(e.response?.data?.message ?? 'Allocation failed'); }
      finally { this.savingAlloc = false; }
    },

    clearAllocAssignee() {
      this.allocForm.employee_id = ''; this.allocForm.employee_name = '';
      this.allocForm.location_id = '';
      this.allocForm.client_id   = ''; this.allocForm.client_name   = '';
      this.empSearch = ''; this.empResults = [];
      this.allocClientSearch = ''; this.allocClientResults = [];
    },

    async searchAllocClients() {
      clearTimeout(this.allocClientTimer);
      if (!this.allocClientSearch.trim()) { this.allocClientResults = []; return; }
      this.allocClientTimer = setTimeout(async () => {
        try {
          const { data } = await axios.get('/hr/clients', { params: { search: this.allocClientSearch, length: 10 } });
          this.allocClientResults = data.data?.data ?? data.data ?? [];
        } catch { /**/ }
      }, 300);
    },

    selectAllocClient(c) {
      this.allocForm.client_id   = c.id;
      this.allocForm.client_name = c.customer_name;
      this.allocClientSearch    = '';
      this.allocClientResults   = [];
    },

    openReturnModal(al) { this.returningAlloc = al; this.returnForm = { condition_at_return: '', return_notes: '' }; this.returnModal = true; },

    async submitReturn() {
      if (!this.returnForm.condition_at_return) { this.toast.warning('Select condition at return'); return; }
      this.savingReturn = true;
      try {
        await axios.put(`/hr/asset/allocation/${this.returningAlloc.id}/return`, this.returnForm);
        this.toast.success('Asset returned');
        this.returnModal = false;
        this.fetchAllocations(); this.fetchAssets(); this.fetchDashboard();
      } catch (e) { this.toast.error(e.response?.data?.message ?? 'Return failed'); }
      finally { this.savingReturn = false; }
    },

    // ── Loss & Damage ──────────────────────────────────────────────
    async fetchLd() {
      this.ldLoading = true;
      try {
        const { data } = await axios.get('/hr/asset/loss-damage', { params: { page: this.ldPage, per_page: 15 } });
        this.ldCases = data.data?.data ?? [];
        this.ldMeta  = { current_page: data.data?.current_page ?? 1, last_page: data.data?.last_page ?? 1 };
      } catch { this.toast.error('Failed to load loss/damage cases'); }
      finally { this.ldLoading = false; }
    },

    openRaiseCaseModal() { this.ldForm = this.blankLdForm(); this.ldEmpSearch = ''; this.ldEmpResults = []; this.ldModal = true; },

    async searchLdEmployees() {
      if (!this.ldEmpSearch.trim()) { this.ldEmpResults = []; return; }
      try {
        const { data } = await axios.get('/hr/chat/employees', { params: { search: this.ldEmpSearch } });
        this.ldEmpResults = data.data ?? [];
      } catch { /**/ }
    },

    selectLdEmployee(e) { this.ldForm.employee_id = e.id; this.ldForm.employee_name = e.name; this.ldEmpSearch = ''; this.ldEmpResults = []; },

    async submitLdCase() {
      if (!this.ldForm.asset_id)     { this.toast.warning('Select an asset'); return; }
      if (!this.ldForm.employee_id)  { this.toast.warning('Select an employee'); return; }
      if (!this.ldForm.incident_date){ this.toast.warning('Incident date is required'); return; }
      if (!this.ldForm.description)  { this.toast.warning('Description is required'); return; }
      this.savingLd = true;
      try {
        await axios.post('/hr/asset/loss-damage', this.ldForm);
        this.toast.success('Loss/damage case raised');
        this.ldModal = false;
        this.fetchLd(); this.fetchAssets();
      } catch (e) { this.toast.error(e.response?.data?.message ?? 'Failed'); }
      finally { this.savingLd = false; }
    },

    async approveLd(c) {
      if (!confirm('Approve this loss/damage case?')) return;
      try {
        await axios.put(`/hr/asset/loss-damage/${c.id}/approve`);
        this.toast.success('Case approved');
        this.fetchLd();
      } catch (e) { this.toast.error(e.response?.data?.message ?? 'Failed'); }
    },

    async rejectLd(c) {
      const reason = prompt('Rejection reason (optional):');
      if (reason === null) return;
      try {
        await axios.put(`/hr/asset/loss-damage/${c.id}/reject`, { rejection_reason: reason });
        this.toast.success('Case rejected');
        this.fetchLd();
      } catch (e) { this.toast.error(e.response?.data?.message ?? 'Failed'); }
    },

    // ── Disposals ──────────────────────────────────────────────────
    async fetchDisposals() {
      this.dispLoading = true;
      try {
        const { data } = await axios.get('/hr/asset/disposal');
        this.disposals = data.data?.data ?? [];
      } catch { this.toast.error('Failed to load disposals'); }
      finally { this.dispLoading = false; }
    },

    openDisposalModal() { this.disposalForm = this.blankDisposalForm(); this.disposalModal = true; },

    async submitDisposal() {
      if (!this.disposalForm.asset_id)       { this.toast.warning('Select an asset'); return; }
      if (!this.disposalForm.disposal_method){ this.toast.warning('Select disposal method'); return; }
      if (!this.disposalForm.disposal_date)  { this.toast.warning('Disposal date required'); return; }
      this.savingDisposal = true;
      try {
        await axios.post('/hr/asset/disposal', this.disposalForm);
        this.toast.success('Disposal record created');
        this.disposalModal = false;
        this.fetchDisposals();
      } catch (e) { this.toast.error(e.response?.data?.message ?? 'Failed'); }
      finally { this.savingDisposal = false; }
    },

    async approveDisposal(d) {
      if (!confirm('Approve this disposal?')) return;
      try {
        await axios.put(`/hr/asset/disposal/${d.id}/approve`);
        this.toast.success('Disposal approved — asset archived');
        this.fetchDisposals(); this.fetchAssets();
      } catch (e) { this.toast.error(e.response?.data?.message ?? 'Failed'); }
    },

    async rejectDisposal(d) {
      try {
        await axios.put(`/hr/asset/disposal/${d.id}/reject`);
        this.toast.success('Disposal rejected');
        this.fetchDisposals();
      } catch (e) { this.toast.error(e.response?.data?.message ?? 'Failed'); }
    },

    // ── Maintenance ────────────────────────────────────────────────
    async fetchMaintenance() {
      this.maintLoading = true;
      try {
        const { data } = await axios.get('/hr/asset/maintenance');
        this.maintenance = data.data?.data ?? [];
      } catch { this.toast.error('Failed to load maintenance logs'); }
      finally { this.maintLoading = false; }
    },

    openMaintenanceModal() { this.maintForm = this.blankMaintForm(); this.maintModal = true; },

    async submitMaintenance() {
      if (!this.maintForm.asset_id)     { this.toast.warning('Select an asset'); return; }
      if (!this.maintForm.issue_type)   { this.toast.warning('Issue type is required'); return; }
      if (!this.maintForm.reported_date){ this.toast.warning('Reported date is required'); return; }
      this.savingMaint = true;
      try {
        await axios.post('/hr/asset/maintenance', this.maintForm);
        this.toast.success('Maintenance log created');
        this.maintModal = false;
        this.fetchMaintenance(); this.fetchAssets();
      } catch (e) { this.toast.error(e.response?.data?.message ?? 'Failed'); }
      finally { this.savingMaint = false; }
    },

    async resolveMaintenance(m) {
      if (!confirm(`Mark "${m.issue_type}" as resolved?`)) return;
      try {
        await axios.put(`/hr/asset/maintenance/${m.id}/resolve`);
        this.toast.success('Maintenance issue resolved');
        this.fetchMaintenance(); this.fetchAssets();
      } catch (e) { this.toast.error(e.response?.data?.message ?? 'Failed'); }
    },

    // ── Depreciation Schedule ──────────────────────────────────────
    async openDeprSchedule(asset) {
      this.deprAsset    = asset;
      this.deprSchedule = [];
      this.deprModal    = true;
      this.deprLoading  = true;
      try {
        const { data } = await axios.get(`/hr/asset/${asset.id}/depreciation-schedule`);
        this.deprAsset    = data.data.asset ?? asset;
        this.deprSchedule = data.data.schedule ?? [];
      } catch (e) {
        this.toast.error(e.response?.data?.message ?? 'Failed to load schedule');
      } finally {
        this.deprLoading = false;
      }
    },

    openRunDeprModal() {
      this.runDeprResult = null;
      this.runDeprModal  = true;
    },

    async submitRunDepreciation() {
      if (!this.runDeprMonth) { this.toast.warning('Select a period month'); return; }
      this.runDeprLoading = true;
      this.runDeprResult  = null;
      try {
        const { data } = await axios.get('/hr/asset/depreciation/run', { params: { month: this.runDeprMonth } });
        const r = data.data;
        this.runDeprResult = { ...r, hasErrors: (r.errors ?? []).length > 0 };
        if (r.processed > 0) {
          this.toast.success(`Depreciation posted for ${r.processed} asset(s)`);
          this.fetchDashboard();
          if (this.activeTab === 'assets') this.fetchAssets();
        } else {
          this.toast.info('No assets required depreciation for this period');
        }
      } catch (e) {
        this.toast.error(e.response?.data?.message ?? 'Failed to run depreciation');
      } finally {
        this.runDeprLoading = false;
      }
    },

    // ── Helpers ────────────────────────────────────────────────────
    closeAllModals() {
      this.viewModal = this.assetModal = this.allocModal = this.returnModal =
      this.ldModal   = this.disposalModal = this.maintModal = this.catModal = false;
    },

    employeeName(emp) {
      if (!emp) return '—';
      return [emp.first_name, emp.last_name].filter(Boolean).join(' ') || emp.name || '—';
    },

    assigneeName(al) {
      if (!al) return '—';
      const type = al.assignee_type ?? 'employee';
      if (type === 'location') return al.location?.name ?? '—';
      if (type === 'client')   return al.client?.customer_name ?? '—';
      return this.employeeName(al.employee);
    },

    assigneeTypeChip(type) {
      if (type === 'location') return 'chip--purple';
      if (type === 'client')   return 'chip--amber';
      return 'chip--blue';
    },

    fmtDate(d) {
      if (!d) return '—';
      return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    },

    fmtCost(v) {
      if (v == null || v === '') return '—';
      return Number(v).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },

    fmtStatus(s) {
      if (!s) return '—';
      return s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    },

    fmtMethod(m) {
      const map = { sale: 'Sale', scrap: 'Scrap', donation: 'Donation', write_off: 'Write-off', insurance_claim: 'Insurance Claim' };
      return map[m] ?? m;
    },

    fmtDepMethod(m) {
      const map = { straight_line: 'Straight-Line', declining_balance: 'Declining Balance', units_of_production: 'Units of Production', none: 'None' };
      return map[m] ?? m ?? '—';
    },

    statusChip(s) {
      const map = {
        available: 'chip--green', allocated: 'chip--blue', in_maintenance: 'chip--amber',
        lost: 'chip--red', damaged: 'chip--red', disposed: 'chip--gray', archived: 'chip--gray',
      };
      return map[s] ?? 'chip--gray';
    },

    conditionChip(c) {
      return c === 'good' ? 'chip--green' : c === 'damaged' ? 'chip--amber' : 'chip--red';
    },

    ldChip(s) {
      const map = { pending: 'chip--amber', approved: 'chip--green', rejected: 'chip--red', deduction_sent: 'chip--blue', recovered: 'chip--gray' };
      return map[s] ?? 'chip--gray';
    },

    disposalChip(s) {
      return s === 'approved' ? 'chip--green' : s === 'rejected' ? 'chip--red' : 'chip--amber';
    },

    maintChip(s) {
      return s === 'resolved' ? 'chip--green' : s === 'in_service' ? 'chip--blue' : 'chip--amber';
    },

    // ── Blank forms ────────────────────────────────────────────────
    blankAssetForm() {
      return { name: '', category_id: '', brand: '', model_no: '', serial_no: '', barcode: '', purchase_date: '', purchase_cost: '', salvage_value: '', vendor_id: '', warranty_expiry: '', location_id: '', acquisition_type: 'purchased', depreciation_method: 'straight_line', useful_life_months: '', notes: '' };
    },

    blankAllocForm() {
      return { asset_id: '', assignee_type: 'employee', employee_id: '', employee_name: '', location_id: '', client_id: '', client_name: '', return_due_date: '', notes: '', _locked_asset: false };
    },

    blankLdForm() {
      return { asset_id: '', employee_id: '', employee_name: '', case_type: 'damaged', incident_date: '', description: '', assessed_cost: '', recovery_amount: '', installments: 1 };
    },

    blankDisposalForm() {
      return { asset_id: '', disposal_method: 'sale', disposal_date: '', sale_value: '', reason: '' };
    },

    blankMaintForm() {
      return { asset_id: '', issue_type: '', reported_date: '', description: '', vendor_id: '', set_in_maintenance: true };
    },

    blankCatForm() {
      return { name: '', parent_id: '', depreciation_method: 'straight_line', default_useful_life_months: '', description: '', is_active: true };
    },

    blankVendorForm() {
      return { name: '', category_id: '', contact_person: '', email: '', phone: '', address: '', is_active: true };
    },
  },
};
</script>

<style scoped>
/* ── Permission denied ────────────────────────────────────────────── */
.permission-denied { display:flex; flex-direction:column; align-items:center; justify-content:center; min-height:60vh; gap:12px; }
.denied-icon  { font-size:48px; color:#d1d5db; }
.denied-title { font-size:20px; font-weight:700; color:#374151; margin:0; }
.denied-sub   { font-size:14px; color:#9ca3af; margin:0; }

/* ── Page header ──────────────────────────────────────────────────── */
.page-header  { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:12px; margin-bottom:20px; }
.page-title   { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.page-sub     { font-size:13px; color:#7a90b0; margin:0; }
.page-actions { display:flex; gap:8px; flex-wrap:wrap; align-items:center; }

/* ── Tab nav ──────────────────────────────────────────────────────── */
.asset-tabs { display:flex; gap:4px; margin-bottom:20px; flex-wrap:wrap; }
.asset-tab  { padding:8px 16px; border:1.5px solid #e4eaf2; border-radius:8px; background:#fff; color:#4a5e78; font-size:13px; font-weight:600; cursor:pointer; transition:all .15s; white-space:nowrap; }
.asset-tab:hover { border-color:#004AAD; color:#004AAD; }
.asset-tab--active { background:#004AAD; border-color:#004AAD; color:#fff; }

/* ── Stat grid ────────────────────────────────────────────────────── */
.stat-grid { display:grid; grid-template-columns:repeat(auto-fill, minmax(200px, 1fr)); gap:16px; }
.stat-card { background:#fff; border-radius:12px; padding:18px 20px; display:flex; align-items:center; gap:14px; box-shadow:0 1px 6px rgba(0,0,0,.06); }
.stat-icon { width:44px; height:44px; border-radius:10px; display:flex; align-items:center; justify-content:center; font-size:18px; flex-shrink:0; }
.stat-icon--blue   { background:rgba(0,74,173,.1);   color:#004AAD; }
.stat-icon--green  { background:rgba(22,163,74,.1);  color:#16a34a; }
.stat-icon--amber  { background:rgba(217,119,6,.1);  color:#d97706; }
.stat-icon--red    { background:rgba(220,38,38,.1);  color:#dc2626; }
.stat-icon--purple { background:rgba(124,58,237,.1); color:#7c3aed; }
.stat-icon--teal   { background:rgba(13,148,136,.1); color:#0d9488; }
.stat-body  { display:flex; flex-direction:column; }
.stat-value { font-size:22px; font-weight:800; color:#1a2b45; line-height:1; }
.stat-label { font-size:12px; color:#7a90b0; margin-top:3px; }

/* ── Buttons ──────────────────────────────────────────────────────── */
.btn-primary-action { display:inline-flex; align-items:center; gap:6px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:0 18px; height:38px; border-radius:8px; border:none; cursor:pointer; white-space:nowrap; }
.btn-primary-action:hover:not(:disabled) { background:#003b8e; }
.btn-primary-action:disabled { opacity:.6; cursor:not-allowed; }
.btn-ghost { display:inline-flex; align-items:center; gap:6px; background:#fff; color:#1a2b45; font-size:13px; font-weight:600; padding:0 16px; height:38px; border-radius:8px; border:1px solid #dde5f0; cursor:pointer; }
.btn-ghost:hover { background:#f0f4f8; }
.btn-icon  { background:none; border:none; cursor:pointer; color:#7a90b0; padding:5px 8px; border-radius:6px; font-size:13px; }
.btn-icon:hover { background:#f0f4f8; color:#004AAD; }
.btn-icon--red   { color:#dc2626 !important; }
.btn-icon--red:hover { background:#fee2e2 !important; }
.btn-icon--green { color:#16a34a !important; }
.btn-icon--green:hover { background:#dcfce7 !important; }

/* ── Section header ───────────────────────────────────────────────── */
.section-header { display:flex; align-items:center; justify-content:space-between; margin-bottom:14px; }
.section-title  { font-size:14px; font-weight:700; color:#1a2b45; margin:0; }

/* ── Filter bar ───────────────────────────────────────────────────── */
.filter-bar { display:flex; flex-wrap:wrap; gap:8px; margin-bottom:14px; }
.filter-input  { flex:1; min-width:180px; height:38px; padding:0 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; outline:none; }
.filter-input:focus { border-color:#004AAD; }
.filter-select { height:38px; padding:0 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; outline:none; cursor:pointer; }

/* ── Table ────────────────────────────────────────────────────────── */
.table-card { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }
.data-table { width:100%; border-collapse:collapse; font-size:13px; }
.data-table thead th { padding:12px 14px; text-align:left; font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.04em; color:#7a90b0; background:#f8fafc; border-bottom:1px solid #e4eaf2; }
.data-table tbody tr { border-bottom:1px solid #f0f4f8; transition:background .1s; }
.data-table tbody tr:hover { background:#f8fafc; }
.data-table tbody td { padding:12px 14px; color:#1a2b45; vertical-align:middle; }
.empty-cell  { text-align:center; color:#aab8cc; padding:40px !important; }
.actions-cell { white-space:nowrap; }

/* ── Chips ────────────────────────────────────────────────────────── */
.chip        { display:inline-block; padding:3px 10px; border-radius:20px; font-size:11px; font-weight:600; }
.chip--green  { background:#dcfce7; color:#16a34a; }
.chip--amber  { background:#fef9c3; color:#ca8a04; }
.chip--red    { background:#fee2e2; color:#dc2626; }
.chip--blue   { background:#dbeafe; color:#1d4ed8; }
.chip--gray   { background:#f1f5f9; color:#64748b; }

/* ── Pagination ───────────────────────────────────────────────────── */
.pagination-bar { display:flex; align-items:center; justify-content:center; gap:12px; padding:14px; border-top:1px solid #f0f4f8; }
.btn-pg  { padding:5px 14px; border:1px solid #dde5f0; border-radius:6px; background:#fff; font-size:13px; cursor:pointer; }
.btn-pg:disabled { opacity:.4; cursor:not-allowed; }
.pg-info { font-size:13px; color:#7a90b0; }

/* ── Skeleton ─────────────────────────────────────────────────────── */
.sk-table { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }
.sk-row   { height:48px; background:linear-gradient(90deg,#f0f4f8 25%,#e4eaf2 50%,#f0f4f8 75%); background-size:200% 100%; animation:shimmer 1.4s infinite; margin-bottom:1px; }
.sk-grid  { display:grid; grid-template-columns:repeat(4,1fr); gap:16px; }
.sk-stat-card { height:80px; border-radius:12px; background:linear-gradient(90deg,#f0f4f8 25%,#e4eaf2 50%,#f0f4f8 75%); background-size:200% 100%; animation:shimmer 1.4s infinite; }
@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }

/* ── Modals ───────────────────────────────────────────────────────── */
.modal-overlay { display:none; position:fixed; inset:0; background:rgba(0,0,0,.45); z-index:1000; align-items:center; justify-content:center; padding:16px; }
.modal-overlay.open { display:flex; }
.modal-box     { background:#fff; border-radius:14px; width:100%; max-width:520px; max-height:90vh; overflow-y:auto; box-shadow:0 20px 60px rgba(0,0,0,.2); }
.modal-box--wide { max-width:680px; }
.modal-header-styled { display:flex; align-items:center; justify-content:space-between; padding:18px 22px; border-bottom:1px solid #f0f4f8; }
.modal-title-styled  { font-size:15px; font-weight:700; color:#1a2b45; }
.modal-close-btn     { background:none; border:none; cursor:pointer; color:#7a90b0; font-size:16px; line-height:1; }
.modal-body-styled   { padding:20px 22px; display:flex; flex-direction:column; gap:14px; }
.modal-footer-styled { display:flex; justify-content:flex-end; gap:8px; padding:16px 22px; border-top:1px solid #f0f4f8; }

/* ── Forms ────────────────────────────────────────────────────────── */
.form-grid-2col  { display:grid; grid-template-columns:1fr 1fr; gap:0 20px; }
.span-2          { grid-column:span 2; }
.form-group-styled  { display:flex; flex-direction:column; gap:5px; }
.form-label-styled  { font-size:12px; font-weight:600; color:#4a5e78; }
.form-input-styled  { width:100%; padding:9px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; outline:none; box-sizing:border-box; background:#fff; }
.form-input-styled:focus { border-color:#004AAD; }

/* ── Asset detail grid ────────────────────────────────────────────── */
.asset-detail-grid { display:grid; grid-template-columns:1fr 1fr; gap:10px 24px; }
.detail-row   { display:flex; flex-direction:column; gap:3px; }
.detail-label { font-size:11px; font-weight:600; color:#94a3b8; text-transform:uppercase; letter-spacing:.04em; }

/* ── Employee search dropdown ─────────────────────────────────────── */
.emp-dropdown { background:#fff; border:1.5px solid #dde5f0; border-radius:8px; margin-top:4px; max-height:180px; overflow-y:auto; z-index:10; }
.emp-option   { padding:9px 12px; font-size:13px; cursor:pointer; color:#1a2b45; }
.emp-option:hover { background:#f4f7fb; }
.selected-employee { margin-top:6px; font-size:13px; color:#1a2b45; display:flex; align-items:center; gap:6px; }
.btn-clear { background:none; border:none; cursor:pointer; color:#94a3b8; font-size:15px; padding:0 4px; }

/* ── Assignee type tabs ───────────────────────────────────────────── */
.assignee-type-tabs { display:flex; gap:6px; margin-top:2px; }
.assignee-tab { padding:6px 14px; border:1.5px solid #dde5f0; border-radius:8px; background:#fff; color:#4a5e78; font-size:12px; font-weight:600; cursor:pointer; transition:all .15s; }
.assignee-tab:hover { border-color:#004AAD; color:#004AAD; }
.assignee-tab--active { background:#004AAD; border-color:#004AAD; color:#fff; }
.chip--sm { font-size:10px !important; padding:2px 7px !important; }
.chip--purple { background:#f3e8ff; color:#7e22ce; }

/* ── Utilities ────────────────────────────────────────────────────── */
.fw-600     { font-weight:600; }
.text-blue  { color:#004AAD; }
.text-green { color:#16a34a; }
.text-red   { color:#dc2626; }
.text-muted-sm { font-size:11px; color:#94a3b8; display:block; margin-top:2px; }
.mt-3 { margin-top:16px; }
.me-1 { margin-right:4px; }
.mb-3 { margin-bottom:12px; }
.req  { color:#e03535; }

/* ── Outline action button ────────────────────────────────────────── */
.btn-outline-action { display:inline-flex; align-items:center; gap:6px; background:#fff; color:#004AAD; font-size:13px; font-weight:600; padding:0 16px; height:38px; border-radius:8px; border:1.5px solid #004AAD; cursor:pointer; white-space:nowrap; }
.btn-outline-action:hover { background:#f0f5ff; }

/* ── Teal icon button (depreciation schedule) ─────────────────────── */
.btn-icon--teal { color:#0d9488 !important; }
.btn-icon--teal:hover { background:#f0fdfa !important; }

/* ── Depreciation schedule modal ──────────────────────────────────── */
.depr-summary-strip { display:flex; flex-wrap:wrap; gap:10px 24px; padding:14px 22px; background:#f8fafc; border-bottom:1px solid #f0f4f8; }
.depr-kv { display:flex; flex-direction:column; gap:2px; }
.depr-k  { font-size:10.5px; font-weight:600; text-transform:uppercase; letter-spacing:.04em; color:#94a3b8; }
.depr-v  { font-size:13px; color:#1a2b45; font-weight:600; }

.depr-loading { padding:32px; text-align:center; color:#7a90b0; font-size:13px; }
.depr-empty   { padding:32px; text-align:center; color:#94a3b8; font-size:13px; }

.depr-table-wrap { padding:16px 22px; max-height:400px; overflow-y:auto; }
.depr-table { width:100%; border-collapse:collapse; font-size:13px; }
.depr-table thead th { padding:9px 12px; font-size:10.5px; font-weight:700; text-transform:uppercase; letter-spacing:.04em; color:#7a90b0; background:#f8fafc; border-bottom:1px solid #e4eaf2; }
.depr-table tbody tr { border-bottom:1px solid #f0f4f8; }
.depr-table tbody td { padding:9px 12px; }
.depr-row--posted { background:#fafffe; }
.depr-row--future { opacity:.75; }
.tr { text-align:right; }
.tc { text-align:center; }

/* ── Run depreciation result ──────────────────────────────────────── */
.depr-result-box { margin-top:12px; border-radius:8px; padding:12px 14px; font-size:13px; line-height:1.8; }
.depr-result--ok   { background:#f0fdf4; border:1px solid #bbf7d0; color:#166534; }
.depr-result--warn { background:#fffbeb; border:1px solid #fde68a; color:#92400e; }
.depr-err-list { margin:4px 0 0 16px; padding:0; font-size:12px; }
</style>
